import { Component, Element, Prop, h } from '@stencil/core';

import { getLocalizedStrings } from '../../utils/utils';
import { injectFont } from '../../utils/utils';
import rpfNavStrings from './locales/translations.json';

interface Link {
  url: string;
  textKey: string;
}

@Component({
  tag: 'rpf-org-nav',
  styleUrl: 'rpf-org-nav.scss',
  shadow: true,
})
export class RpfOrgNav {
  @Element() hostElement: HTMLElement;
  @Prop() locale: string;
  @Prop() loggedIn: boolean;
  @Prop() authAnchors: boolean = false;
  @Prop({ reflect: true }) theme: string;
  @Prop() logInPath: string;
  @Prop() logOutPath: string;
  @Prop() signUpPath: string;
  @Prop() logInToken: string;
  @Prop() signUpToken: string;
  @Prop() logInTokenUrl: string;
  @Prop() signUpTokenUrl: string;
  @Prop() hideAccountDropdown: boolean;
  @Prop() hideSignUp: boolean;
  @Prop() hideLogIn: boolean;

  linkStrings: Record<string, string>;
  links: Link[] = [
    { url: '/learn', textKey: 'learn' },
    { url: '/teach', textKey: 'teach' },
    { url: '/research', textKey: 'research' },
    { url: 'https://www.raspberrypi.com', textKey: 'computers' },
    { url: '/about', textKey: 'about' },
  ];

  componentWillLoad() {
    injectFont();
    this.checkUserSession();
  }

  async componentWillRender() {
    this.linkStrings = getLocalizedStrings(this.locale, rpfNavStrings);
    this.links.map((link: any) => {
      link.text = this.linkStrings[link.textKey];
    });
  }

  async checkUserSession() {
    try {
      const response = await fetch('/session/logged_in');

      if (response.status === 204) {
        this.loggedIn = true;
      }
    } catch (error) {
      console.error('Error checking user session:', error);
    }
  }

  handleBurgerMenuClick() {
    const component = this.hostElement.shadowRoot.querySelector('.rpf-org-nav');
    component.classList.toggle('open');
  }

  rpfAuth(mobile: boolean = false) {
    return (
      <rpf-auth
        locale={this.locale}
        logInToken={this.logInToken}
        signUpToken={this.signUpToken}
        logInTokenUrl={this.logInTokenUrl}
        signUpTokenUrl={this.signUpTokenUrl}
        loggedIn={this.loggedIn}
        authAnchors={this.authAnchors}
        logInPath={this.logInPath}
        logOutPath={this.logOutPath}
        signUpPath={this.signUpPath}
        hideSignUp={this.hideSignUp}
        hideLogIn={this.hideLogIn}
        hideAccountDropdown={this.hideAccountDropdown}
        theme={this.theme}
        mobileMenu={mobile}
      ></rpf-auth>
    );
  }

  rpfLinks() {
    return <rpf-links links={JSON.stringify(this.links)}></rpf-links>;
  }

  rpfBurgerMenu() {
    return (
      <button class="rpf-burger-menu__button" aria-label={this.linkStrings['toggle-navigation-menu']} type="button" onClick={() => this.handleBurgerMenuClick()}>
        <span class="rpf-burger-menu__icon" aria-hidden="true"></span>
        <span class="rpf-burger-menu__icon" aria-hidden="true"></span>
        <span class="rpf-burger-menu__icon" aria-hidden="true"></span>
      </button>
    );
  }

  headerSkipper() {
    return (
      <ul class="site-header-skipper">
        <li class="site-header-skipper__item">
          <a
            class="site-header-skipper__link rpf-link"
            data-event-action="click"
            data-event-category="Header: Skip links"
            data-event-label="Skip to main content"
            href="#rp-app-landmark-main"
          >
            {this.linkStrings['skip-to-content']}
          </a>
        </li>
        <li class="site-header-skipper__item">
          <a
            class="site-header-skipper__link rpf-link"
            data-event-action="click"
            data-event-category="Header: Skip links"
            data-event-label="Skip to footer"
            href="#rp-app-landmark-footer"
          >
            {this.linkStrings['skip-to-footer']}
          </a>
        </li>
        <li class="site-header-skipper__item">
          <a
            class="site-header-skipper__link rpf-link"
            data-event-action="click"
            data-event-category="Header: Skip links"
            data-event-label="Accessibility statement and help"
            href="/accessibility/"
          >
            {this.linkStrings['accessibility-statement']}
          </a>
        </li>
      </ul>
    );
  }

  render() {
    return (
      <div>
        {this.headerSkipper()}
        <header class="rpf-org-nav">
          <div class="rpf-org-nav__start-slot">
            <rpf-logo theme={this.theme}></rpf-logo>
            <nav class="rpf-org-nav__nav--lg">{this.rpfLinks()}</nav>
            <a
              class="rpf-org-nav__button rpf-button rpf-button--sm"
              data-event-action="click"
              data-event-category="Header: Navigation"
              data-event-label="Donate"
              data-section="donate"
              href="/donate"
            >
              {this.linkStrings['donate']}
            </a>
            <div class="rpf-org-nav__menu rpf-burger-menu">{this.rpfBurgerMenu()}</div>
          </div>
          <div class="rpf-org-nav__end-slot">{this.rpfAuth()}</div>
          <nav class="rpf-org-nav__nav--sm">
            {this.rpfLinks()}
            {this.rpfAuth(true)}
          </nav>
        </header>
      </div>
    );
  }
}
